import { stripBom } from "./csv";

/**
 * vCard 3.0/4.0 reader (KTD2: hand-rolled, no new dependency). Covers what
 * Apple, Google and Android actually emit: folded continuation lines,
 * `group.PROP;PARAM=value:value` lines, bare 2.1-style parameters,
 * QUOTED-PRINTABLE with a CHARSET, and multi-card files.
 *
 * `value` is left escaped; use `unescapeValue` for a plain value or
 * `splitComponents` for a structured one (`N`, `ADR`, `ORG`).
 */

export interface VCardProperty {
	/** The `item1` in `item1.EMAIL:…`, when present. */
	readonly group?: string | undefined;
	/** Uppercased property name. */
	readonly name: string;
	/** Uppercased parameter name to its values; bare parameters become `TYPE`. */
	readonly params: Readonly<Record<string, string[]>>;
	readonly value: string;
}

export interface VCardFile {
	readonly cards: VCardProperty[][];
	/** 1-based line of a `BEGIN:VCARD` with no matching `END:VCARD`. */
	readonly unterminatedLine?: number | undefined;
}

const encoder = new TextEncoder();

/** Joins continuation lines (a leading space or tab) onto the previous line. */
const unfold = (text: string): string[] => {
	const lines: string[] = [];
	for (const line of stripBom(text).split(/\r\n|\r|\n/)) {
		const last = lines.length - 1;
		if (last >= 0 && (line.startsWith(" ") || line.startsWith("\t"))) {
			lines[last] = `${lines[last] ?? ""}${line.slice(1)}`;
		} else {
			lines.push(line);
		}
	}
	return lines;
};

const decodeQuotedPrintable = (raw: string, charset: string): string => {
	const bytes: number[] = [];
	for (let i = 0; i < raw.length; i += 1) {
		const character = raw.charAt(i);
		const hex = raw.slice(i + 1, i + 3);
		if (character === "=" && /^[0-9A-Fa-f]{2}$/.test(hex)) {
			bytes.push(Number.parseInt(hex, 16));
			i += 2;
			continue;
		}
		for (const byte of encoder.encode(character)) bytes.push(byte);
	}
	const buffer = new Uint8Array(bytes);
	try {
		return new TextDecoder(charset).decode(buffer);
	} catch {
		return new TextDecoder().decode(buffer);
	}
};

/** Index of the `:` that ends the property head, ignoring quoted parameters. */
const headEnd = (line: string): number => {
	let quoted = false;
	for (let i = 0; i < line.length; i += 1) {
		const character = line.charAt(i);
		if (character === '"') quoted = !quoted;
		else if (character === ":" && !quoted) return i;
	}
	return -1;
};

/** Splits the property head on unquoted semicolons. */
const splitHead = (head: string): string[] => {
	const parts: string[] = [];
	let current = "";
	let quoted = false;
	for (let i = 0; i < head.length; i += 1) {
		const character = head.charAt(i);
		if (character === '"') quoted = !quoted;
		if (character === ";" && !quoted) {
			parts.push(current);
			current = "";
			continue;
		}
		current += character;
	}
	parts.push(current);
	return parts;
};

const unquote = (value: string): string =>
	value.startsWith('"') && value.endsWith('"') && value.length > 1
		? value.slice(1, -1)
		: value;

const parseProperty = (line: string): VCardProperty | undefined => {
	const colon = headEnd(line);
	if (colon <= 0) return undefined;
	const [first = "", ...rest] = splitHead(line.slice(0, colon));
	const dot = first.indexOf(".");
	const params: Record<string, string[]> = {};
	for (const segment of rest) {
		if (segment === "") continue;
		const equals = segment.indexOf("=");
		const key = (equals === -1 ? "TYPE" : segment.slice(0, equals))
			.trim()
			.toUpperCase();
		const raw = equals === -1 ? segment : segment.slice(equals + 1);
		const values = (params[key] ??= []);
		for (const value of raw.split(",")) values.push(unquote(value.trim()));
	}
	const encoding = (params.ENCODING ?? []).join(",").toUpperCase();
	const raw = line.slice(colon + 1);
	return {
		group: dot === -1 ? undefined : first.slice(0, dot),
		name: (dot === -1 ? first : first.slice(dot + 1)).trim().toUpperCase(),
		params,
		value: encoding.includes("QUOTED-PRINTABLE")
			? decodeQuotedPrintable(raw, params.CHARSET?.[0] ?? "utf-8")
			: raw,
	};
};

export const readVCards = (text: string): VCardFile => {
	const cards: VCardProperty[][] = [];
	let current: VCardProperty[] | undefined;
	let beginLine: number | undefined;

	unfold(text).forEach((line, index) => {
		const marker = line.trim().toUpperCase();
		if (marker === "BEGIN:VCARD") {
			current = [];
			beginLine = index + 1;
			return;
		}
		if (marker === "END:VCARD") {
			if (current) cards.push(current);
			current = undefined;
			beginLine = undefined;
			return;
		}
		if (!current || line.trim() === "") return;
		const property = parseProperty(line);
		if (property) current.push(property);
	});

	return { cards, unterminatedLine: current ? beginLine : undefined };
};

export const parseVCards = (text: string): VCardProperty[][] =>
	readVCards(text).cards;

/** Undoes `\,` `\;` `\\` and `\n` / `\N`. */
export const unescapeValue = (value: string): string =>
	value.replace(/\\(.)/g, (_, character: string) =>
		character === "n" || character === "N" ? "\n" : character,
	);

/** Splits a structured value on unescaped semicolons, unescaping each part. */
export const splitComponents = (value: string): string[] => {
	const parts: string[] = [];
	let current = "";
	for (let i = 0; i < value.length; i += 1) {
		const character = value.charAt(i);
		if (character === "\\") {
			current += character + value.charAt(i + 1);
			i += 1;
			continue;
		}
		if (character === ";") {
			parts.push(current);
			current = "";
			continue;
		}
		current += character;
	}
	parts.push(current);
	return parts.map(unescapeValue);
};
